
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search as SearchIcon, X, ArrowLeft, FilterX } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ProductsGrid from '@/components/home/ProductsGrid';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { useProducts } from '@/hooks/useProducts';
import TranslatedText from '@/TranslatedText';

// Define a consistent Product interface
interface Product {
  id: string;
  name: string;
  price: number;
  currency: string;
  image: string;
  description: string;
  discount?: number;
  couponCode?: string;
}

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || '';
  const subcategoryParam = searchParams.get('subcategory') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { products, isLoading: productsLoading } = useProducts();

  useEffect(() => {
    // When component mounts, focus the search input if there's no category filter
    if (!categoryParam) {
      const searchInput = document.getElementById('search-input');
      if (searchInput) {
        searchInput.focus();
      }
    }
  }, [categoryParam]);

  useEffect(() => {
    if (productsLoading) {
      setIsLoading(true);
      return;
    }

    setIsLoading(true);
    
    // Format products to match our Product interface
    const formattedProducts = products.map(product => ({
      id: product.id,
      name: product.name,
      price: product.price,
      currency: 'RWF',
      image: product.imageUrl,
      description: product.description,
      discount: product.discount,
      couponCode: product.couponCode
    }));

    // Filter products based on search query and/or category/subcategory
    let filteredProducts = formattedProducts;
    
    // Apply category filter if provided
    if (categoryParam) {
      filteredProducts = filteredProducts.filter(product => {
        const productCategory = products.find(p => p.id === product.id)?.category || '';
        return productCategory.toLowerCase() === categoryParam.toLowerCase();
      });
      
      // Apply subcategory filter if provided
      if (subcategoryParam) {
        filteredProducts = filteredProducts.filter(product => {
          const productSubcategory = products.find(p => p.id === product.id)?.subcategory || '';
          return productSubcategory.toLowerCase() === subcategoryParam.toLowerCase();
        });
      }
    }
    
    // Apply search query filter if provided
    if (query) {
      filteredProducts = filteredProducts.filter(product => 
        product.name.toLowerCase().includes(query.toLowerCase()) || 
        product.description.toLowerCase().includes(query.toLowerCase())
      );
    }
    
    // Show trending/popular products when no filters
    if (!query && !categoryParam) {
      // Limited to 10 for trending section
      filteredProducts = formattedProducts.slice(0, 10);
    }
    
    setSearchResults(filteredProducts);
    setIsLoading(false);
  }, [query, products, productsLoading, categoryParam, subcategoryParam]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newParams = new URLSearchParams();
    
    if (searchQuery.trim()) {
      newParams.set('q', searchQuery.trim());
    }
    
    if (categoryParam) {
      newParams.set('category', categoryParam);
    }
    
    if (subcategoryParam) {
      newParams.set('subcategory', subcategoryParam);
    }
    
    setSearchParams(newParams);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    
    const newParams = new URLSearchParams();
    if (categoryParam) {
      newParams.set('category', categoryParam);
    }
    if (subcategoryParam) {
      newParams.set('subcategory', subcategoryParam);
    }
    
    setSearchParams(newParams);
    
    const searchInput = document.getElementById('search-input');
    if (searchInput) {
      searchInput.focus();
    }
  };

  const clearFilters = () => {
    navigate('/search');
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 pt-20 pb-10 py-[44px]">
        <div className="flex flex-col">
          {/* Search header */}
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-ahavex-blue" aria-label="Go back">
              <ArrowLeft size={24} />
            </Button>
            
            <form onSubmit={handleSubmit} className="flex-1">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <SearchIcon size={18} className="text-gray-400" />
                </div>
                <Input 
                  id="search-input" 
                  type="text" 
                  placeholder="Search for products..." 
                  value={searchQuery} 
                  onChange={handleInputChange} 
                  className="pl-10 pr-10 py-2 bg-gray-50 border-gray-200 rounded-full w-full" 
                />
                {searchQuery && (
                  <button 
                    type="button" 
                    onClick={handleClearSearch} 
                    className="absolute inset-y-0 right-3 flex items-center" 
                    aria-label="Clear search"
                  >
                    <X size={18} className="text-gray-400" />
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Search results */}
          <div className="mt-2">
            {isLoading ? (
              <div className="flex justify-center items-center p-10">
                <motion.div 
                  className="w-6 h-6 border-2 border-ahavex-blue border-t-transparent rounded-full" 
                  animate={{
                    rotate: 360
                  }} 
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: "linear"
                  }} 
                />
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-ahavex-blue">
                    {categoryParam ? (
                      <TranslatedText 
                        text={`${categoryParam}${subcategoryParam ? ` > ${subcategoryParam}` : ''} (${searchResults.length})`} 
                      />
                    ) : query ? (
                      <TranslatedText text={`Search Results (${searchResults.length})`} />
                    ) : (
                      <TranslatedText text="Trending Products" />
                    )}
                  </h2>
                  
                  {(categoryParam || query) && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs"
                      onClick={clearFilters}
                    >
                      <FilterX size={14} className="mr-1" /> 
                      <TranslatedText text="Clear Filters" />
                    </Button>
                  )}
                </div>

                {searchResults.length > 0 ? (
                  <ProductsGrid products={searchResults} />
                ) : (
                  <div className="text-center py-10">
                    <div className="inline-flex justify-center items-center w-20 h-20 mb-4 rounded-full bg-gray-100">
                      <SearchIcon size={32} className="text-gray-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      <TranslatedText text="No results found" />
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      {query ? (
                        <TranslatedText 
                          text={`We couldn't find any products matching "${query}". Try different keywords or browse our categories.`}
                        />
                      ) : categoryParam ? (
                        <TranslatedText 
                          text={`No products found in ${categoryParam}${subcategoryParam ? ` > ${subcategoryParam}` : ''}. Browse other categories or check back later.`}
                        />
                      ) : (
                        <TranslatedText text="No products available at the moment. Please check back later." />
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Search;
